import React, { useEffect, useState } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";
import { Link } from "react-router-dom";

const VerifiedBuddies = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [verifiedProviders, setVerifiedProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "suspended"
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState({});
  const itemsPerPage = 20;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  useEffect(() => {
    const fetchVerifiedProviders = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${BASE_URL}admin/providers/verified?page=1&limit=500`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const data = await response.json();
        if (data.status === "success") {
          setVerifiedProviders(data.data || []);
          setFilteredProviders(data.data || []);
          setCurrentPage(1);
        }
      } catch (err) {
        console.error("Error fetching verified providers:", err);
      }
      setLoading(false);
    };
    fetchVerifiedProviders();
  }, [successMsg]);

  useEffect(() => {
    let list = verifiedProviders;
    if (statusFilter === "active") {
      list = list.filter((p) => !p.isSuspended);
    } else if (statusFilter === "suspended") {
      list = list.filter((p) => p.isSuspended);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (provider) =>
          provider.name?.toLowerCase().includes(q) ||
          provider.phone?.includes(q) ||
          provider.email?.toLowerCase().includes(q) ||
          provider.providerSubServices?.some((s) => s.name?.toLowerCase().includes(q))
      );
    }
    setFilteredProviders(list);
    setCurrentPage(1);
  }, [searchQuery, statusFilter, verifiedProviders]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProviders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProviders = filteredProviders.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSuspendUnsuspend = async (e, providerId, isSuspended) => {
    e.preventDefault();
    e.stopPropagation();

    setActionLoading((prev) => ({ ...prev, [providerId]: true }));

    try {
      const action = isSuspended ? "unsuspend" : "suspend";
      const res = await fetch(
        `${BASE_URL}admin/providers/${providerId}/${action}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const data = await res.json();
      if (data.status === "success") {
        // Update provider state immediately
        setVerifiedProviders((prev) =>
          prev.map((p) =>
            p.id === providerId
              ? {
                  ...p,
                  isSuspended: data.data.isSuspended,
                  status: data.data.status,
                }
              : p,
          ),
        );
        setSuccessMsg(data.message || "Action successful");
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error("Error:", err);
    }

    setActionLoading((prev) => ({ ...prev, [providerId]: false }));
  };

  const totalVerifiedCount = verifiedProviders.length;
  const activeCount = verifiedProviders.filter((p) => !p.isSuspended).length;
  const suspendedCount = verifiedProviders.filter((p) => p.isSuspended).length;
  const servicesEnabledCount = verifiedProviders.filter((p) => (p.providerSubServices || []).length > 0).length;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen main-content">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-md border-b border-stone-200/80 px-4 lg:px-8 py-5 sticky top-0 z-40 shadow-xs">
          <div className="pt-14 lg:pt-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F8D346] animate-pulse shadow-xs shadow-amber-400" />
                  <span className="text-xs font-bold text-amber-800/90 uppercase tracking-wider">
                    Live Provider Registry
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-stone-900 text-[#F8D346] border border-stone-800 flex items-center justify-center shrink-0 shadow-xs">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-xl lg:text-2xl font-black text-stone-950 tracking-tight leading-none">
                      Verified Buddies
                    </h1>
                    <p className="text-xs text-stone-500 font-medium mt-1">
                      Manage, monitor and audit all KYC-approved platform service providers
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <div className="bg-[#F8D346]/15 border border-[#F8D346]/40 text-amber-950 px-3.5 py-2 rounded-xl text-xs font-black shadow-2xs flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span>{activeCount} Active Online</span>
                </div>

                <div className="bg-white border border-stone-200 text-stone-600 px-3.5 py-2 rounded-xl text-xs font-bold shadow-2xs">
                  Updated Today
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-8">
          {successMsg && (
            <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-black flex items-center gap-2.5 animate-fadeIn">
              <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{successMsg}</span>
            </div>
          )}

          {/* 4 Stats Cards (100% Real Data) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            {/* 1. Total Verified */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                  Total Verified
                </span>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200/70 flex items-center justify-center shrink-0 shadow-2xs">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-3xl font-black text-stone-900 tracking-tight">
                  {totalVerifiedCount.toLocaleString()}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-400 font-semibold">KYC Status</span>
                  <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/70">
                    100% Approved
                  </span>
                </div>
              </div>
            </div>

            {/* 2. Active & Ready */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                  Active Providers
                </span>
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-800 border border-amber-200/70 flex items-center justify-center shrink-0 shadow-2xs">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-3xl font-black text-stone-900 tracking-tight">
                  {activeCount.toLocaleString()}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-400 font-semibold">Operational Rate</span>
                  <span className="text-xs font-black text-amber-900 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/70">
                    {totalVerifiedCount > 0 ? Math.round((activeCount / totalVerifiedCount) * 100) : 0}% Ready
                  </span>
                </div>
              </div>
            </div>

            {/* 3. Suspended Accounts */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                  Restricted / Suspended
                </span>
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-700 border border-rose-200/70 flex items-center justify-center shrink-0 shadow-2xs">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-3xl font-black text-stone-900 tracking-tight">
                  {suspendedCount.toLocaleString()}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-400 font-semibold">Account Safety</span>
                  <span className={`text-xs font-black px-2 py-0.5 rounded-md border ${
                    suspendedCount === 0
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200/70"
                      : "bg-rose-50 text-rose-700 border-rose-200/70"
                  }`}>
                    {suspendedCount === 0 ? "Good Standing" : `${suspendedCount} Restricted`}
                  </span>
                </div>
              </div>
            </div>

            {/* 4. Services Enabled */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm hover:shadow-md transition-all p-5 lg:p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                  Skills & Services
                </span>
                <div className="w-10 h-10 rounded-xl bg-stone-100 text-stone-700 border border-stone-200/70 flex items-center justify-center shrink-0 shadow-2xs">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="text-3xl font-black text-stone-900 tracking-tight">
                  {servicesEnabledCount.toLocaleString()}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-400 font-semibold">Service Coverage</span>
                  <span className="text-xs font-black text-stone-800 bg-stone-100 px-2 py-0.5 rounded-md border border-stone-200/70">
                    Ready For Dispatch
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Search & Segmented Filter Bar */}
          <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-4 lg:p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[260px] max-w-md">
              <svg
                className="absolute left-3.5 top-3 w-4 h-4 text-stone-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search verified buddy by name, phone, email, or service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-xl text-xs text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]/40 focus:border-[#F8D346] transition-all placeholder:text-stone-400"
              />
            </div>

            {/* Segmented Filter Pills */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-stone-400 mr-1">
                Filter:
              </span>
              {[
                { key: "all", label: "All Verified", count: totalVerifiedCount },
                { key: "active", label: "Active", count: activeCount },
                { key: "suspended", label: "Restricted", count: suspendedCount },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setStatusFilter(opt.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all duration-200 flex items-center gap-1.5 ${
                    statusFilter === opt.key
                      ? "bg-gradient-to-r from-[#F8D346] to-[#F5C518] text-stone-950 border border-[#F5C518] shadow-sm shadow-amber-300/30"
                      : "bg-stone-100 hover:bg-stone-200/70 text-stone-600 border border-stone-200/60"
                  }`}
                >
                  <span>{opt.label}</span>
                  <span className="text-[10px] font-bold opacity-75">
                    ({opt.count})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Verified Providers List Card */}
          <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm overflow-hidden mb-8">
            <div className="p-5 lg:p-6 border-b border-stone-100 flex flex-wrap items-center justify-between gap-3 bg-stone-50/40">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base lg:text-lg font-black text-stone-900 tracking-tight">
                    All Verified Providers
                  </h2>
                  <span className="bg-[#F8D346]/20 text-amber-900 border border-[#F8D346]/40 px-2 py-0.5 rounded-full text-[11px] font-black">
                    {filteredProviders.length} Matches
                  </span>
                </div>
                <p className="text-xs text-stone-500 font-medium mt-1">
                  Click on any provider card to view KYC documents, earnings, and detailed profile
                </p>
              </div>

              <div className="text-xs font-bold text-stone-400 bg-white border border-stone-200/80 px-3 py-1.5 rounded-xl shadow-2xs">
                Page {currentPage} of {totalPages || 1}
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-stone-200 border-t-[#F8D346] mb-3"></div>
                <span className="text-xs font-black text-stone-600 uppercase tracking-wider">
                  Loading verified providers...
                </span>
              </div>
            ) : verifiedProviders.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-16 h-16 bg-stone-100 rounded-2xl border border-stone-200 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-stone-900 font-black text-base mb-1">
                  No Verified Providers Found
                </p>
                <p className="text-xs text-stone-500 font-medium max-w-sm mx-auto">
                  Verified providers will appear here once approved through the KYC pipeline.
                </p>
              </div>
            ) : filteredProviders.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-16 h-16 bg-stone-100 rounded-2xl border border-stone-200 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <p className="text-stone-900 font-black text-base mb-1">
                  No Results Match Search
                </p>
                <p className="text-xs text-stone-500 font-medium max-w-sm mx-auto">
                  Try adjusting your query or resetting the filter options.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {currentProviders.map((provider) => {
                  const initial = (provider.name?.trim()?.charAt(0) || "P").toUpperCase();
                  const subServices = provider.providerSubServices || [];

                  return (
                    <div
                      key={provider.id}
                      className="p-5 lg:p-6 hover:bg-stone-50/80 transition-all duration-200 group flex flex-col md:flex-row md:items-center md:justify-between gap-5"
                    >
                      {/* Left: Avatar + Details link */}
                      <Link
                        to={`/provider/${provider.id}/verified`}
                        state={{ provider: { ...provider, type: "verified" } }}
                        className="flex items-start sm:items-center gap-4 min-w-0 flex-1"
                      >
                        {/* Avatar */}
                        <div className="relative shrink-0 w-12 h-12">
                          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-stone-800 bg-stone-900 text-[#F8D346] flex items-center justify-center shadow-xs group-hover:border-[#F8D346] transition-colors duration-200">
                            {provider.profileImage ? (
                              <img
                                src={provider.profileImage}
                                alt={provider.name || "Provider"}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <span className="font-black text-base text-[#F8D346] select-none">
                                {initial}
                              </span>
                            )}
                          </div>
                          <span
                            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[9px] ring-2 ring-white font-black shadow-xs pointer-events-none"
                            title="Verified Provider"
                          >
                            ✓
                          </span>
                        </div>

                        {/* Text info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <h3 className="text-base font-black text-stone-900 group-hover:text-amber-900 transition-colors tracking-tight truncate">
                              {provider.name || "Unnamed Provider"}
                            </h3>
                            <div className="flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200/80 px-2 py-0.5 rounded-md text-[10px] font-black">
                                ✓ Verified
                              </span>
                              {provider.isSuspended ? (
                                <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200/80 px-2 py-0.5 rounded-md text-[10px] font-black">
                                  Restricted
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 border border-stone-200 px-2 py-0.5 rounded-md text-[10px] font-bold">
                                  Active
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Contact Chips */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-stone-500 mb-2">
                            <div className="inline-flex items-center gap-1.5 font-semibold text-stone-700">
                              <svg className="w-3.5 h-3.5 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span>{provider.phone || "N/A"}</span>
                            </div>

                            {provider.email && (
                              <div className="inline-flex items-center gap-1.5 font-medium text-stone-600 truncate max-w-xs">
                                <svg className="w-3.5 h-3.5 text-stone-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span className="truncate">{provider.email}</span>
                              </div>
                            )}

                            <span className="text-[11px] font-semibold text-stone-400">
                              Joined {provider.createdAt ? new Date(provider.createdAt).toLocaleDateString() : "N/A"}
                            </span>
                          </div>

                          {/* Sub-Services Tags */}
                          {subServices.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              {subServices.slice(0, 4).map((s, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 px-2 py-0.5 rounded-md text-[10px] font-black border border-stone-200/60"
                                >
                                  {s.name || "Service"}
                                </span>
                              ))}
                              {subServices.length > 4 && (
                                <span className="text-[10px] font-bold text-stone-400">
                                  +{subServices.length - 4} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </Link>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2.5 shrink-0 self-end md:self-center">
                        <Link
                          to={`/provider/${provider.id}/verified`}
                          state={{ provider: { ...provider, type: "verified" } }}
                          className="inline-flex items-center gap-1.5 bg-stone-900 group-hover:bg-[#F8D346] text-white group-hover:text-stone-950 px-3.5 py-2 rounded-xl text-xs font-black shadow-xs transition-all duration-200"
                        >
                          <span>View Profile</span>
                          <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>

                        <button
                          onClick={(e) =>
                            handleSuspendUnsuspend(
                              e,
                              provider.id,
                              provider.isSuspended,
                            )
                          }
                          disabled={actionLoading[provider.id]}
                          className={`px-3 py-2 rounded-xl text-xs font-black transition-all shadow-2xs ${
                            provider.isSuspended
                              ? "bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white border border-emerald-200/80 disabled:opacity-50"
                              : "bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-200/80 disabled:opacity-50"
                          }`}
                        >
                          {actionLoading[provider.id]
                            ? "..."
                            : provider.isSuspended
                              ? "Unsuspend"
                              : "Suspend"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination Controls */}
            {filteredProviders.length > 0 && (
              <div className="p-4 lg:p-5 border-t border-stone-200/80 bg-stone-50/40 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs font-bold text-stone-500">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredProviders.length)} of{" "}
                  {filteredProviders.length} buddies
                </div>

                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-black transition-all ${
                      currentPage === 1
                        ? "border-stone-200 text-stone-300 cursor-not-allowed bg-white"
                        : "border-stone-200 bg-white text-stone-700 hover:border-[#F8D346] hover:bg-[#F8D346]/10 shadow-2xs"
                    }`}
                  >
                    ← Previous
                  </button>

                  {/* Page Numbers */}
                  {Array.from(
                    { length: Math.min(5, totalPages) },
                    (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      const isActive = currentPage === pageNum;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-xl border text-xs font-black transition-all flex items-center justify-center ${
                            isActive
                              ? "bg-gradient-to-r from-[#F8D346] to-[#F5C518] text-stone-950 border-[#F5C518] shadow-xs shadow-amber-300/30"
                              : "border-stone-200 bg-white text-stone-700 hover:bg-stone-100/80 shadow-2xs"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-black transition-all ${
                      currentPage === totalPages
                        ? "border-stone-200 text-stone-300 cursor-not-allowed bg-white"
                        : "border-stone-200 bg-white text-stone-700 hover:border-[#F8D346] hover:bg-[#F8D346]/10 shadow-2xs"
                    }`}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifiedBuddies;
